import { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { useTenant } from "../../../../hooks/useTenant";
import WorkoutPrescriptionService from "../../../../services/workout_prescription";

/**
 * Hook para gerenciar prescrições de treino (CRUD + prescrição em lote).
 */
export const usePrescriptions = () => {
    const { idTenant, user: authUser } = useTenant();

    const [prescriptions, setPrescriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const loadPrescriptions = useCallback(async (idMember = null) => {
        if (!idTenant) return;
        setLoading(true);
        try {
            let data;
            if (idMember) {
                data = await WorkoutPrescriptionService.getByMember(idTenant, idMember);
            } else {
                data = []; // Não buscar todas as prescrições do tenant para economizar leitura
            }
            setPrescriptions(data || []);
            setError(null);
        } catch (err) {
            setError(err.message || "Erro ao carregar prescrições.");
        } finally {
            setLoading(false);
        }
    }, [idTenant]);

    useEffect(() => {
        loadPrescriptions();
    }, [loadPrescriptions]);

    /**
     * Prescrever um modelo para um ou mais alunos.
     * @param {Object} prescriptionBase - { idTemplate, templateTitle, workoutType, blocks, startDate, endDate, weeklyFrequency }
     * @param {Array}  membersList      - [{ id, name, photo }]
     */
    const handlePrescribe = async (prescriptionBase, membersList) => {
        if (!idTenant || !authUser) return null;
        setSaving(true);
        try {
            const results = await WorkoutPrescriptionService.prescribe(
                prescriptionBase,
                membersList,
                idTenant,
                authUser
            );
            if (membersList && membersList.length > 0) {
                await loadPrescriptions(membersList[0].id);
            } else {
                await loadPrescriptions();
            }
            const count = results.length;
            toast.success(`Treino prescrito para ${count} aluno${count > 1 ? "s" : ""}!`);
            return results;
        } catch (err) {
            setError(err.message || "Erro ao prescrever treino.");
            toast.error(err.message || "Erro ao prescrever treino.");
            return null;
        } finally {
            setSaving(false);
        }
    };

    const handleUpdate = async (prescriptionId, updateData, idMember) => {
        if (!idTenant || !authUser || !prescriptionId || !idMember) return null;
        setSaving(true);
        try {
            const updated = await WorkoutPrescriptionService.update(
                prescriptionId,
                updateData,
                idTenant,
                authUser,
                idMember
            );
            await loadPrescriptions(idMember);
            toast.success("Prescrição atualizada!");
            return updated;
        } catch (err) {
            setError(err.message || "Erro ao atualizar prescrição.");
            toast.error(err.message || "Erro ao atualizar prescrição.");
            return null;
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (prescriptionId, prescriptionTitle, idMember) => {
        if (!idTenant || !authUser || !prescriptionId || !idMember) return false;
        setSaving(true);
        try {
            await WorkoutPrescriptionService.delete(prescriptionId, idTenant, authUser, prescriptionTitle, idMember);
            await loadPrescriptions(idMember);
            toast.success("Prescrição removida!");
            return true;
        } catch (err) {
            setError(err.message || "Erro ao deletar prescrição.");
            toast.error(err.message || "Erro ao deletar prescrição.");
            return false;
        } finally {
            setSaving(false);
        }
    };

    return {
        prescriptions,
        loading,
        saving,
        error,
        reload: loadPrescriptions,
        handlePrescribe,
        handleUpdate,
        handleDelete,
    };
};
